import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, ArrowRight, Calendar, Users, Package, Save, Search, Wine, X, Trash2, Plus, ChevronDown, Tag, Crown, Shield, Star, Filter, UserPlus, Eye, Bell, Mail, Pencil, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Input } from '../components/SubscriptionAppInput';
import { Card } from '../components/SubscriptionAppCard';
import { EditorToolbar } from '../components/EditorToolbar';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Divider } from '../components/SubscriptionAppDivider';
// Types
interface AllocationFormData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  products: string[]; // Product IDs
  segments: string[]; // Segment IDs (Legacy)
  status: 'Draft' | 'Active' | 'Scheduled';
}
interface Product {
  id: string;
  name: string;
  variant: string;
  sku: string;
  price: number;
  stock: number;
}
interface SelectedProduct extends Product {
  allocationQty: number;
  minPerCustomer: number;
  maxPerCustomer: number;
  priceOverride: string; // empty string means use original
}
interface Rule {
  id: string;
  field: string;
  operator: string;
  value: string;
}
interface Member {
  id: string;
  name: string;
  email: string;
  tier: string;
}
const steps = [{
  id: 'details',
  label: 'Details',
  number: 1,
  icon: Calendar
}, {
  id: 'products',
  label: 'Products',
  number: 2,
  icon: Package
}, {
  id: 'eligibility',
  label: 'Eligibility',
  number: 3,
  icon: Users
}, {
  id: 'review',
  label: 'Review',
  number: 4,
  icon: Check
}];
// Mock Data
const availableProducts: Product[] = [{
  id: '1',
  name: '2023 Reserve Shiraz',
  variant: '750ml',
  sku: 'SHZ-23-RES',
  price: 85.0,
  stock: 480
}, {
  id: '2',
  name: '2022 Chardonnay',
  variant: '750ml',
  sku: 'CHD-22-STD',
  price: 45.0,
  stock: 320
}, {
  id: '3',
  name: '2018 Cabernet Sauvignon',
  variant: '750ml',
  sku: 'CAB-18-VNT',
  price: 120.0,
  stock: 120
}, {
  id: '4',
  name: 'Sparkling Rosé',
  variant: '750ml',
  sku: 'ROS-SPK-NV',
  price: 35.0,
  stock: 600
}, {
  id: '5',
  name: '2021 Pinot Noir',
  variant: '750ml',
  sku: 'PN-21-EST',
  price: 55.0,
  stock: 215
}, {
  id: '6',
  name: '2023 Sauvignon Blanc',
  variant: '750ml',
  sku: 'SB-23-CRL',
  price: 28.0,
  stock: 850
}, {
  id: '7',
  name: '2020 Merlot',
  variant: '750ml',
  sku: 'MER-20-RES',
  price: 42.0,
  stock: 150
}, {
  id: '8',
  name: 'Grand Reserve Box',
  variant: '6 Pack',
  sku: 'BOX-GR-6',
  price: 450.0,
  stock: 45
}];
const availableSegments = [{
  id: 'gold',
  name: 'Gold Members',
  count: 485
}, {
  id: 'silver',
  name: 'Silver Members',
  count: 850
}, {
  id: 'club',
  name: 'Club Members',
  count: 1200
}, {
  id: 'vip',
  name: 'VIPs',
  count: 150
}];
const mockMembers: Member[] = [{
  id: 'm1',
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  tier: 'Sommelier'
}, {
  id: 'm2',
  name: 'Michael Chen',
  email: 'michael.c@example.com',
  tier: 'Connoisseur'
}, {
  id: 'm3',
  name: 'Emma Williams',
  email: 'emma.w@example.com',
  tier: 'Vintner'
}, {
  id: 'm4',
  name: 'James Anderson',
  email: 'james.a@example.com',
  tier: 'Enthusiast'
}, {
  id: 'm5',
  name: 'Olivia Martinez',
  email: 'olivia.m@example.com',
  tier: 'Sommelier'
}, {
  id: 'm6',
  name: 'William Taylor',
  email: 'w.taylor@example.com',
  tier: 'Connoisseur'
}, {
  id: 'm7',
  name: 'Sophia Brown',
  email: 'sophia.b@example.com',
  tier: 'Enthusiast'
}, {
  id: 'm8',
  name: 'Liam Wilson',
  email: 'liam.w@example.com',
  tier: 'Vintner'
}];
const availableTags = ['VIP', 'Premium', 'Cellar Door Signup', 'Wine Club 2024', 'Event Attendee', 'Birthday Month', 'Referral', 'High Value'];
const tiers = [{
  id: 'enthusiast',
  name: 'Enthusiast',
  color: '#10B981',
  bg: '#D1FAE5'
}, {
  id: 'connoisseur',
  name: 'Connoisseur',
  color: '#3B82F6',
  bg: '#DBEAFE'
}, {
  id: 'sommelier',
  name: 'Sommelier',
  color: '#8B5CF6',
  bg: '#EDE9FE'
}, {
  id: 'vintner',
  name: 'Vintner',
  color: '#F59E0B',
  bg: '#FEF3C7'
}];
export function CreateAllocationPage() {
  const navigate = useNavigate();
  const {
    id: editId
  } = useParams<{
    id: string;
  }>();
  const isEditMode = Boolean(editId);
  const [activeStep, setActiveStep] = useState<number>(1);
  const [formData, setFormData] = useState<AllocationFormData>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    products: [],
    segments: [],
    status: 'Draft'
  });
  // Product Selection State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([{
    ...availableProducts.find(p => p.id === '1')!,
    allocationQty: 200,
    minPerCustomer: 1,
    maxPerCustomer: 6,
    priceOverride: '75.00'
  }, {
    ...availableProducts.find(p => p.id === '3')!,
    allocationQty: 50,
    minPerCustomer: 1,
    maxPerCustomer: 3,
    priceOverride: ''
  }]);
  // Eligibility State
  const [activeMethods, setActiveMethods] = useState<Set<string>>(new Set(['tier']));
  const [selectedTiers, setSelectedTiers] = useState<string[]>(['sommelier', 'vintner']);
  const [earlyAccessEnabled, setEarlyAccessEnabled] = useState(false);
  const [earlyAccessTier, setEarlyAccessTier] = useState<string>('vintner');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [rules, setRules] = useState<Rule[]>([{
    id: '1',
    field: 'Subscription Status',
    operator: 'is',
    value: 'Active'
  }, {
    id: '2',
    field: 'Lifetime Spend',
    operator: 'greater than',
    value: '$500'
  }]);
  const [rulesLogic, setRulesLogic] = useState<'AND' | 'OR'>('AND');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  // Review Step State
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [notifyInvitation, setNotifyInvitation] = useState(true);
  const [notifyReminder, setNotifyReminder] = useState(false);
  const [reminderDays, setReminderDays] = useState(3);
  const [notifyConfirmation, setNotifyConfirmation] = useState(true);
  const [notifyAdminClaimed, setNotifyAdminClaimed] = useState(true);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // Sync selected products to formData
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      products: selectedProducts.map(p => p.id)
    }));
  }, [selectedProducts]);
  // Handlers
  const handleNext = () => {
    if (activeStep < steps.length) {
      setActiveStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep(prev => prev - 1);
    }
  };
  const handleStepClick = (stepNumber: number) => {
    if (stepNumber < activeStep || stepNumber === activeStep + 1) {
      setActiveStep(stepNumber);
    }
  };
  const handlePublish = () => {
    setShowPublishModal(true);
  };
  const confirmPublish = () => {
    console.log('Publishing allocation:', {
      ...formData,
      notifications: {
        invitation: notifyInvitation,
        reminder: notifyReminder ? reminderDays : false,
        confirmation: notifyConfirmation,
        adminClaimed: notifyAdminClaimed
      },
      template: saveAsTemplate ? templateName : null
    });
    setShowPublishModal(false);
    setTimeout(() => {
      navigate('/allocations');
    }, 500);
  };
  const handleSaveDraft = () => {
    console.log('Saving draft:', formData);
    navigate('/allocations');
  };
  const updateFormData = (field: keyof AllocationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Product Handlers
  const handleAddProduct = (product: Product) => {
    setSelectedProducts(prev => [...prev, {
      ...product,
      allocationQty: 0,
      minPerCustomer: 1,
      maxPerCustomer: 1,
      priceOverride: ''
    }]);
    setSearchQuery('');
  };
  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };
  const handleUpdateProduct = (productId: string, field: keyof SelectedProduct, value: any) => {
    setSelectedProducts(prev => prev.map(p => {
      if (p.id === productId) {
        return {
          ...p,
          [field]: value
        };
      }
      return p;
    }));
  };
  // Eligibility Handlers
  const toggleMethod = (method: string) => {
    setActiveMethods(prev => {
      const newSet = new Set(prev);
      if (newSet.has(method)) {
        newSet.delete(method);
      } else {
        newSet.add(method);
      }
      return newSet;
    });
  };
  const toggleTier = (tierId: string) => {
    setSelectedTiers(prev => prev.includes(tierId) ? prev.filter(id => id !== tierId) : [...prev, tierId]);
  };
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    setTagDropdownOpen(false);
  };
  const addRule = () => {
    const newRule: Rule = {
      id: Math.random().toString(36).substr(2, 9),
      field: 'Lifetime Spend',
      operator: 'greater than',
      value: ''
    };
    setRules([...rules, newRule]);
  };
  const removeRule = (id: string) => {
    setRules(rules.filter(r => r.id !== id));
  };
  const updateRule = (id: string, field: keyof Rule, value: string) => {
    setRules(rules.map(r => r.id === id ? {
      ...r,
      [field]: value
    } : r));
  };
  const addMember = (member: Member) => {
    if (!selectedMembers.find(m => m.id === member.id)) {
      setSelectedMembers([...selectedMembers, member]);
    }
    setMemberSearchQuery('');
  };
  const removeMember = (id: string) => {
    setSelectedMembers(selectedMembers.filter(m => m.id !== id));
  };
  // Render Steps
  const renderDetailsStep = () => <div className="max-w-2xl mx-auto space-y-6" data-id="element-5775">
      <Card data-id="element-5776">
        <div className="p-6 space-y-6" data-id="element-5777">
          <div data-id="element-5778">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-4" data-id="element-5779">
              Allocation Details
            </h2>
            <div className="space-y-4" data-id="element-5780">
              <div data-id="element-5781">
                <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-5782">
                  Allocation Name
                </label>
                <Input value={formData.name} onChange={e => updateFormData('name', e.target.value)} placeholder="e.g. 2024 Summer Release" data-id="element-5783" />
              </div>
              <div data-id="element-5784">
                <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-5785">
                  Description
                </label>
                <textarea className="w-full rounded-md border border-[#CBD5E1] p-3 text-sm focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] outline-none min-h-[100px]" value={formData.description} onChange={e => updateFormData('description', e.target.value)} placeholder="Describe this allocation for your members..." data-id="element-5786" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-5787">
                <div data-id="element-5788">
                  <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-5789">
                    Start Date
                  </label>
                  <Input type="date" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} data-id="element-5790" />
                </div>
                <div data-id="element-5791">
                  <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-5792">
                    End Date
                  </label>
                  <Input type="date" value={formData.endDate} onChange={e => updateFormData('endDate', e.target.value)} data-id="element-5793" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
  const renderProductsStep = () => {
    const filteredProducts = searchQuery ? availableProducts.filter(p => (p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase())) && !selectedProducts.some(sp => sp.id === p.id)) : [];
    const totalAllocation = selectedProducts.reduce((sum, p) => sum + (Number(p.allocationQty) || 0), 0);
    return <div className="max-w-3xl mx-auto space-y-6" data-id="element-5794">
        <Card padding="none" data-id="element-5795">
          <div className="p-6 space-y-6" data-id="element-5796">
            <div className="space-y-4" data-id="element-5797">
              <div className="relative" data-id="element-5798">
                <Input placeholder="Search products by name or SKU..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-5800" />} data-id="element-5799" />
              </div>

              {searchQuery && <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#E2E8F0] overflow-hidden bg-white shadow-sm" data-id="element-5801">
                  {filteredProducts.length > 0 ? filteredProducts.map(product => <div key={product.id} onClick={() => handleAddProduct(product)} className="flex items-center gap-4 p-3 hover:bg-[#F8FAFC] cursor-pointer transition-colors" data-id="element-5802">
                        <div className="w-10 h-10 rounded bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0" data-id="element-5803">
                          <Wine className="w-5 h-5 text-[#94A3B8]" data-id="element-5804" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-5805">
                          <div className="flex items-center gap-2" data-id="element-5806">
                            <span className="font-medium text-[#0F172A] text-sm" data-id="element-5807">
                              {product.name}
                            </span>
                            <span className="text-xs text-[#64748B]" data-id="element-5808">
                              {product.variant}
                            </span>
                          </div>
                          <div className="text-xs text-[#64748B]" data-id="element-5809">
                            SKU: {product.sku} • Stock: {product.stock}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-[#0EA5E9]" data-id="element-5810">
                          Add
                        </div>
                      </div>) : <div className="p-4 text-center text-sm text-[#64748B]" data-id="element-5811">
                      No products found matching "{searchQuery}"
                    </div>}
                </div>}
            </div>

            <Divider data-id="element-5812" />

            <div data-id="element-5813">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-4" data-id="element-5814">
                Selected Products ({selectedProducts.length})
              </h3>

              {selectedProducts.length > 0 ? <div className="space-y-3" data-id="element-5815">
                  {selectedProducts.map(product => <div key={product.id} className="border border-[#E2E8F0] rounded-lg p-4 flex flex-col md:flex-row gap-4 items-start md:items-center bg-white" data-id="element-5816">
                      <div className="flex items-center gap-4 flex-1 min-w-[240px]" data-id="element-5817">
                        <div className="w-12 h-12 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0" data-id="element-5818">
                          <Wine className="w-6 h-6 text-[#94A3B8]" data-id="element-5819" />
                        </div>
                        <div data-id="element-5820">
                          <div className="font-semibold text-[#0F172A] text-sm" data-id="element-5821">
                            {product.name}
                          </div>
                          <div className="text-[13px] text-[#64748B]" data-id="element-5822">
                            {product.variant}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-end gap-4" data-id="element-5823">
                        <div data-id="element-5824">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5825">
                            Allocation Qty
                          </label>
                          <Input type="number" value={product.allocationQty.toString()} onChange={e => handleUpdateProduct(product.id, 'allocationQty', parseInt(e.target.value) || 0)} className="w-24" data-id="element-5826" />
                          <div className="text-[11px] text-[#64748B] mt-1" data-id="element-5827">
                            {product.stock} available
                          </div>
                        </div>

                        <div data-id="element-5828">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5829">
                            Per Customer
                          </label>
                          <div className="flex items-center gap-2" data-id="element-5830">
                            <div className="w-16" data-id="element-5831">
                              <Input type="number" placeholder="Min" value={product.minPerCustomer.toString()} onChange={e => handleUpdateProduct(product.id, 'minPerCustomer', parseInt(e.target.value) || 0)} data-id="element-5832" />
                            </div>
                            <span className="text-[#94A3B8]" data-id="element-5833">-</span>
                            <div className="w-16" data-id="element-5834">
                              <Input type="number" placeholder="Max" value={product.maxPerCustomer.toString()} onChange={e => handleUpdateProduct(product.id, 'maxPerCustomer', parseInt(e.target.value) || 0)} data-id="element-5835" />
                            </div>
                          </div>
                          <div className="text-[11px] text-[#64748B] mt-1 invisible" data-id="element-5836">
                            Placeholder
                          </div>
                        </div>

                        <div data-id="element-5837">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5838">
                            Price Override
                          </label>
                          <div className="w-32" data-id="element-5839">
                            <Input placeholder={`Original: $${product.price.toFixed(2)}`} value={product.priceOverride} onChange={e => handleUpdateProduct(product.id, 'priceOverride', e.target.value)} icon={<span className="text-[#94A3B8] text-sm" data-id="element-5841">
                                  $
                                </span>} data-id="element-5840" />
                          </div>
                          <div className="text-[11px] text-[#64748B] mt-1 invisible" data-id="element-5842">
                            Placeholder
                          </div>
                        </div>

                        <div className="pb-6" data-id="element-5843">
                          <IconButton icon={<X className="w-4 h-4" data-id="element-5845" />} onClick={() => handleRemoveProduct(product.id)} variant="ghost" className="text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2]" data-id="element-5844" />
                        </div>
                      </div>
                    </div>)}

                  <div className="text-[14px] text-[#64748B] pt-2 px-1" data-id="element-5846">
                    Total allocation:{' '}
                    <span className="font-medium text-[#0F172A]" data-id="element-5847">
                      {totalAllocation} units
                    </span>{' '}
                    across {selectedProducts.length} products
                  </div>
                </div> : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-[#E2E8F0] rounded-lg bg-[#F8FAFC]" data-id="element-5848">
                  <div className="w-12 h-12 rounded-full bg-[#E2E8F0] flex items-center justify-center mb-3" data-id="element-5849">
                    <Package className="w-6 h-6 text-[#94A3B8]" data-id="element-5850" />
                  </div>
                  <p className="text-[14px] text-[#94A3B8] italic" data-id="element-5851">
                    Search above to add products to this allocation.
                  </p>
                </div>}
            </div>
          </div>
        </Card>
      </div>;
  };
  const renderEligibilityStep = () => {
    // Calculate mock member count based on selections
    const baseCount = 485;
    let count = baseCount;
    if (activeMethods.has('tier')) count = Math.min(count, selectedTiers.length * 120);
    if (activeMethods.has('tag')) count = Math.min(count, selectedTags.length * 50 || count);
    if (activeMethods.has('manual')) count += selectedMembers.length;
    return <div className="max-w-3xl mx-auto space-y-6" data-id="element-5852">
        <Card padding="none" data-id="element-5853">
          <div className="p-6 space-y-6" data-id="element-5854">
            {/* Header */}
            <div data-id="element-5855">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-1" data-id="element-5856">
                Member Eligibility
              </h2>
              <p className="text-sm text-[#64748B]" data-id="element-5857">
                Define who can access this allocation
              </p>
            </div>

            {/* Method Tabs */}
            <div className="flex flex-wrap gap-2" data-id="element-5858">
              {[{
              id: 'tier',
              label: 'By Tier'
            }, {
              id: 'tag',
              label: 'By Tag'
            }, {
              id: 'rules',
              label: 'Custom Rules'
            }, {
              id: 'manual',
              label: 'Manual Select'
            }].map(method => {
              const isActive = activeMethods.has(method.id);
              return <button key={method.id} onClick={() => toggleMethod(method.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${isActive ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC] hover:text-[#0F172A]'}`} data-id="element-5859">
                    {method.label}
                  </button>;
            })}
            </div>

            <Divider data-id="element-5860" />

            {/* Content Sections */}
            <div className="space-y-8" data-id="element-5861">
              {/* BY TIER */}
              {activeMethods.has('tier') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-5862">
                  <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-5863">
                    Loyalty Tiers
                  </h3>
                  <div className="flex flex-wrap gap-3" data-id="element-5864">
                    {tiers.map(tier => {
                  const isSelected = selectedTiers.includes(tier.id);
                  return <button key={tier.id} onClick={() => toggleTier(tier.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${isSelected ? 'text-white border-transparent shadow-sm' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]'}`} style={{
                    backgroundColor: isSelected ? tier.color : undefined
                  }} data-id="element-5865">
                          {isSelected && <Check className="w-3.5 h-3.5" data-id="element-5866" />}
                          {tier.name}
                        </button>;
                })}
                  </div>

                  {/* Early Access */}
                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0] mt-4" data-id="element-5867">
                    <div className="flex items-start gap-3" data-id="element-5868">
                      <div className="flex items-center h-5" data-id="element-5869">
                        <input id="earlyAccess" type="checkbox" checked={earlyAccessEnabled} onChange={e => setEarlyAccessEnabled(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-5870" />
                      </div>
                      <div className="flex-1" data-id="element-5871">
                        <label htmlFor="earlyAccess" className="text-sm font-medium text-[#0F172A] block" data-id="element-5872">
                          Enable early access for top tier
                        </label>
                        <p className="text-xs text-[#64748B] mt-1" data-id="element-5873">
                          Selected top tier gets access 48 hours before start
                          date
                        </p>

                        {earlyAccessEnabled && <div className="mt-3 max-w-xs" data-id="element-5874">
                            <select value={earlyAccessTier} onChange={e => setEarlyAccessTier(e.target.value)} className="w-full h-9 px-3 text-sm bg-white border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9]" data-id="element-5875">
                              {tiers.filter(t => selectedTiers.includes(t.id)).map(tier => <option key={tier.id} value={tier.id} data-id="element-5876">
                                    {tier.name}
                                  </option>)}
                            </select>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>}

              {/* BY TAG */}
              {activeMethods.has('tag') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-5877">
                  <div className="flex items-center justify-between" data-id="element-5878">
                    <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-5879">
                      Member Tags
                    </h3>
                  </div>

                  <div className="relative" data-id="element-5880">
                    <div className="w-full min-h-[40px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-md flex items-center flex-wrap gap-2 cursor-pointer" onClick={() => setTagDropdownOpen(!tagDropdownOpen)} data-id="element-5881">
                      {selectedTags.length === 0 && <span className="text-sm text-[#94A3B8]" data-id="element-5882">
                          Select tags...
                        </span>}
                      {selectedTags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 rounded bg-[#E0F2FE] text-[#0284C7] text-xs font-medium border border-[#BAE6FD]" data-id="element-5883">
                          {tag}
                          <button onClick={e => {
                      e.stopPropagation();
                      toggleTag(tag);
                    }} className="ml-1.5 hover:text-[#0369A1]" data-id="element-5884">
                            <X className="w-3 h-3" data-id="element-5885" />
                          </button>
                        </span>)}
                      <div className="ml-auto" data-id="element-5886">
                        <ChevronDown className="w-4 h-4 text-[#94A3B8]" data-id="element-5887" />
                      </div>
                    </div>

                    {tagDropdownOpen && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto p-2" data-id="element-5888">
                        {availableTags.map(tag => <div key={tag} onClick={() => toggleTag(tag)} className="flex items-center px-3 py-2 hover:bg-[#F8FAFC] rounded-md cursor-pointer text-sm text-[#0F172A]" data-id="element-5889">
                            <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${selectedTags.includes(tag) ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-5890">
                              {selectedTags.includes(tag) && <Check className="w-3 h-3 text-white" data-id="element-5891" />}
                            </div>
                            {tag}
                          </div>)}
                      </div>}
                  </div>
                </div>}

              {/* CUSTOM RULES */}
              {activeMethods.has('rules') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-5892">
                  <div className="flex items-center justify-between" data-id="element-5893">
                    <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-5894">
                      Custom Rules
                    </h3>
                  </div>

                  <div className="space-y-0" data-id="element-5895">
                    {rules.map((rule, index) => <div key={rule.id} data-id="element-5896">
                        <div className="flex items-center gap-3" data-id="element-5897">
                          <select value={rule.field} onChange={e => updateRule(rule.id, 'field', e.target.value)} className="flex-1 h-10 px-3 pr-8 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_10px_center] bg-no-repeat" data-id="element-5898">
                            <option data-id="element-5899">Lifetime Spend</option>
                            <option data-id="element-5900">Tenure</option>
                            <option data-id="element-5901">Subscription Status</option>
                            <option data-id="element-5902">Region</option>
                            <option data-id="element-5903">Order Count</option>
                          </select>
                          <select value={rule.operator} onChange={e => updateRule(rule.id, 'operator', e.target.value)} className="w-40 h-10 px-3 pr-8 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_10px_center] bg-no-repeat" data-id="element-5904">
                            <option data-id="element-5905">is</option>
                            <option data-id="element-5906">is not</option>
                            <option data-id="element-5907">greater than</option>
                            <option data-id="element-5908">less than</option>
                          </select>
                          {rule.field === 'Subscription Status' ? <select value={rule.value} onChange={e => updateRule(rule.id, 'value', e.target.value)} className="flex-1 h-10 px-3 pr-8 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_10px_center] bg-no-repeat" data-id="element-5909">
                              <option data-id="element-5910">Active</option>
                              <option data-id="element-5911">Paused</option>
                              <option data-id="element-5912">Cancelled</option>
                            </select> : <input type="text" value={rule.value} onChange={e => updateRule(rule.id, 'value', e.target.value)} className="flex-1 h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9]" placeholder="Value..." data-id="element-5913" />}
                          <button onClick={() => removeRule(rule.id)} className="p-2 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded-md transition-colors" data-id="element-5914">
                            <Trash2 className="w-4 h-4" data-id="element-5915" />
                          </button>
                        </div>

                        {/* AND/OR toggle between consecutive rules */}
                        {index < rules.length - 1 && <div className="flex items-center justify-center py-2" data-id="element-5916">
                            <div className="flex bg-[#F1F5F9] rounded-full p-0.5" data-id="element-5917">
                              <button onClick={() => setRulesLogic('AND')} className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${rulesLogic === 'AND' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-5918">
                                AND
                              </button>
                              <button onClick={() => setRulesLogic('OR')} className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${rulesLogic === 'OR' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-5919">
                                OR
                              </button>
                            </div>
                          </div>}
                      </div>)}
                  </div>

                  <button onClick={addRule} className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] flex items-center gap-1" data-id="element-5920">
                    <Plus className="w-4 h-4" data-id="element-5921" /> Add Rule
                  </button>
                </div>}

              {/* MANUAL SELECT */}
              {activeMethods.has('manual') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-5922">
                  <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-5923">
                    Manual Selection
                  </h3>

                  <div className="relative" data-id="element-5924">
                    <Input placeholder="Search members by name or email..." value={memberSearchQuery} onChange={e => setMemberSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-5926" />} data-id="element-5925" />

                    {memberSearchQuery && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto divide-y divide-[#F1F5F9]" data-id="element-5927">
                        {mockMembers.filter(m => m.name.toLowerCase().includes(memberSearchQuery.toLowerCase()) || m.email.toLowerCase().includes(memberSearchQuery.toLowerCase())).filter(m => !selectedMembers.find(sm => sm.id === m.id)).map(member => <div key={member.id} onClick={() => addMember(member)} className="p-3 hover:bg-[#F8FAFC] cursor-pointer flex items-center justify-between" data-id="element-5928">
                              <div data-id="element-5929">
                                <div className="text-sm font-medium text-[#0F172A]" data-id="element-5930">
                                  {member.name}
                                </div>
                                <div className="text-xs text-[#64748B]" data-id="element-5931">
                                  {member.email}
                                </div>
                              </div>
                              <span className="text-xs px-2 py-1 rounded bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]" data-id="element-5932">
                                {member.tier}
                              </span>
                            </div>)}
                      </div>}
                  </div>

                  {selectedMembers.length > 0 && <div className="space-y-2" data-id="element-5933">
                      <div className="text-xs font-medium text-[#64748B]" data-id="element-5934">
                        {selectedMembers.length} members manually added
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-5935">
                        {selectedMembers.map(member => <div key={member.id} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-full shadow-sm" data-id="element-5936">
                            <div className="w-5 h-5 rounded-full bg-[#E0F2FE] text-[#0369A1] flex items-center justify-center text-xs font-bold" data-id="element-5937">
                              {member.name.charAt(0)}
                            </div>
                            <span className="text-sm text-[#0F172A]" data-id="element-5938">
                              {member.name}
                            </span>
                            <button onClick={() => removeMember(member.id)} className="text-[#94A3B8] hover:text-[#EF4444]" data-id="element-5939">
                              <X className="w-3.5 h-3.5" data-id="element-5940" />
                            </button>
                          </div>)}
                      </div>
                    </div>}
                </div>}
            </div>
          </div>

          {/* Bottom Preview Box */}
          <div className="bg-[#E0F2FE] border-t border-[#BAE6FD] p-4 flex items-center justify-between" data-id="element-5941">
            <div className="flex items-center gap-2 text-[#0369A1]" data-id="element-5942">
              <Users className="w-4 h-4" data-id="element-5943" />
              <span className="text-sm font-medium" data-id="element-5944">
                {count} members match these criteria
              </span>
            </div>
            <button className="text-sm font-medium text-[#0284C7] hover:underline" data-id="element-5945">
              Preview Members
            </button>
          </div>
        </Card>
      </div>;
  };
  const renderReviewStep = () => <div className="max-w-3xl mx-auto space-y-6" data-id="element-5946">
      <Card data-id="element-5947">
        <div className="p-6 border-b border-[#E2E8F0]" data-id="element-5948">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-5949">
            Review Allocation
          </h2>
        </div>
        <div className="p-6 space-y-8" data-id="element-5950">
          {/* Details Summary */}
          <div data-id="element-5951">
            <div className="flex items-center justify-between mb-3" data-id="element-5952">
              <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5953">
                Details
              </h3>
              <button onClick={() => handleStepClick(1)} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1 transition-colors" data-id="element-5954">
                <Pencil className="w-3 h-3" data-id="element-5955" /> Edit
              </button>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0] space-y-2" data-id="element-5956">
              <div className="flex justify-between" data-id="element-5957">
                <span className="text-sm text-[#64748B]" data-id="element-5958">Name</span>
                <span className="text-sm font-medium text-[#0F172A]" data-id="element-5959">
                  {formData.name || 'Untitled Allocation'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-5960">
                <span className="text-sm text-[#64748B]" data-id="element-5961">Dates</span>
                <span className="text-sm font-medium text-[#0F172A]" data-id="element-5962">
                  {formData.startDate || 'TBD'} — {formData.endDate || 'TBD'}
                </span>
              </div>
            </div>
          </div>

          {/* Products Summary */}
          <div data-id="element-5963">
            <div className="flex items-center justify-between mb-3" data-id="element-5964">
              <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5965">
                Products ({formData.products.length})
              </h3>
              <button onClick={() => handleStepClick(2)} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1 transition-colors" data-id="element-5966">
                <Pencil className="w-3 h-3" data-id="element-5967" /> Edit
              </button>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] divide-y divide-[#E2E8F0]" data-id="element-5968">
              {formData.products.length === 0 ? <div className="p-4 text-sm text-[#94A3B8] italic" data-id="element-5969">
                  No products selected
                </div> : formData.products.map(id => {
              const product = availableProducts.find(p => p.id === id);
              return <div key={id} className="p-3 flex justify-between items-center" data-id="element-5970">
                      <span className="text-sm font-medium text-[#0F172A]" data-id="element-5971">
                        {product?.name}
                      </span>
                      <span className="text-sm text-[#64748B]" data-id="element-5972">
                        ${product?.price.toFixed(2)}
                      </span>
                    </div>;
            })}
            </div>
          </div>

          {/* Eligibility Summary */}
          <div data-id="element-5973">
            <div className="flex items-center justify-between mb-3" data-id="element-5974">
              <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5975">
                Eligibility
              </h3>
              <button onClick={() => handleStepClick(3)} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1 transition-colors" data-id="element-5976">
                <Pencil className="w-3 h-3" data-id="element-5977" /> Edit
              </button>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] p-4 space-y-4" data-id="element-5978">
              {/* Active Methods */}
              <div className="flex flex-wrap gap-2" data-id="element-5979">
                {Array.from(activeMethods).map(method => <span key={method} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0F172A] text-white" data-id="element-5980">
                    {method === 'tier' ? 'By Tier' : method === 'tag' ? 'By Tag' : method === 'rules' ? 'Custom Rules' : 'Manual Select'}
                  </span>)}
              </div>

              {/* Tiers */}
              {activeMethods.has('tier') && selectedTiers.length > 0 && <div data-id="element-5981">
                  <div className="text-xs text-[#64748B] mb-2 uppercase tracking-wide font-medium" data-id="element-5982">
                    Tiers
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-5983">
                    {selectedTiers.map(tierId => {
                  const tier = tiers.find(t => t.id === tierId);
                  return <span key={tierId} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border" style={{
                    backgroundColor: tier?.bg,
                    color: tier?.color,
                    borderColor: tier?.bg
                  }} data-id="element-5984">
                          {tier?.name}
                        </span>;
                })}
                  </div>
                  {earlyAccessEnabled && <div className="mt-2 text-xs text-[#0EA5E9] flex items-center gap-1" data-id="element-5985">
                      <Crown className="w-3 h-3" data-id="element-5986" />
                      Early access for{' '}
                      {tiers.find(t => t.id === earlyAccessTier)?.name}
                    </div>}
                </div>}

              {/* Tags */}
              {activeMethods.has('tag') && selectedTags.length > 0 && <div data-id="element-5987">
                  <div className="text-xs text-[#64748B] mb-2 uppercase tracking-wide font-medium" data-id="element-5988">
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-5989">
                    {selectedTags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 rounded bg-[#E0F2FE] text-[#0284C7] text-xs font-medium border border-[#BAE6FD]" data-id="element-5990">
                        {tag}
                      </span>)}
                  </div>
                </div>}

              {/* Rules & Manual */}
              <div className="flex gap-6" data-id="element-5991">
                {activeMethods.has('rules') && <div data-id="element-5992">
                    <div className="text-xs text-[#64748B] mb-1 uppercase tracking-wide font-medium" data-id="element-5993">
                      Rules
                    </div>
                    <div className="text-sm font-medium text-[#0F172A]" data-id="element-5994">
                      {rules.length} active rules
                    </div>
                  </div>}
                {activeMethods.has('manual') && <div data-id="element-5995">
                    <div className="text-xs text-[#64748B] mb-1 uppercase tracking-wide font-medium" data-id="element-5996">
                      Manual
                    </div>
                    <div className="text-sm font-medium text-[#0F172A]" data-id="element-5997">
                      {selectedMembers.length} members
                    </div>
                  </div>}
              </div>

              <div className="pt-3 border-t border-[#E2E8F0]" data-id="element-5998">
                <div className="flex items-center justify-between" data-id="element-5999">
                  <span className="text-sm text-[#64748B]" data-id="element-6000">
                    Total Eligible Members
                  </span>
                  <span className="text-sm font-bold text-[#0EA5E9]" data-id="element-6001">~485</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div data-id="element-6002">
            <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider mb-3" data-id="element-6003">
              Notifications
            </h3>
            <div className="bg-white rounded-lg border border-[#E2E8F0] divide-y divide-[#E2E8F0]" data-id="element-6004">
              {/* Invitation */}
              <div className="p-4 flex items-start gap-3" data-id="element-6005">
                <div className="flex items-center h-5 mt-0.5" data-id="element-6006">
                  <input id="notifyInvitation" type="checkbox" checked={notifyInvitation} onChange={e => setNotifyInvitation(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-6007" />
                </div>
                <div className="flex-1" data-id="element-6008">
                  <label htmlFor="notifyInvitation" className="text-sm font-medium text-[#0F172A] block" data-id="element-6009">
                    Send invitation email when allocation opens
                  </label>
                  <p className="text-xs text-[#64748B] mt-0.5" data-id="element-6010">
                    Members receive an email with allocation details and a
                    purchase link
                  </p>
                </div>
              </div>

              {/* Reminder */}
              <div className="p-4 flex items-start gap-3" data-id="element-6011">
                <div className="flex items-center h-5 mt-0.5" data-id="element-6012">
                  <input id="notifyReminder" type="checkbox" checked={notifyReminder} onChange={e => setNotifyReminder(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-6013" />
                </div>
                <div className="flex-1" data-id="element-6014">
                  <div className="flex items-center flex-wrap gap-2" data-id="element-6015">
                    <label htmlFor="notifyReminder" className="text-sm font-medium text-[#0F172A]" data-id="element-6016">
                      Send reminder before allocation closes
                    </label>
                    {notifyReminder && <div className="flex items-center gap-2 ml-2 animate-in fade-in duration-200" data-id="element-6017">
                        <span className="text-sm text-[#0F172A]" data-id="element-6018">
                          Send reminder
                        </span>
                        <input type="number" value={reminderDays} onChange={e => setReminderDays(parseInt(e.target.value) || 1)} className="w-12 h-7 px-1 text-center text-sm border border-[#CBD5E1] rounded-md focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] outline-none" min="1" data-id="element-6019" />
                        <span className="text-sm text-[#0F172A]" data-id="element-6020">
                          days before close
                        </span>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Confirmation */}
              <div className="p-4 flex items-start gap-3" data-id="element-6021">
                <div className="flex items-center h-5 mt-0.5" data-id="element-6022">
                  <input id="notifyConfirmation" type="checkbox" checked={notifyConfirmation} onChange={e => setNotifyConfirmation(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-6023" />
                </div>
                <div className="flex-1" data-id="element-6024">
                  <label htmlFor="notifyConfirmation" className="text-sm font-medium text-[#0F172A] block" data-id="element-6025">
                    Send confirmation email on purchase
                  </label>
                </div>
              </div>

              {/* Admin Notification */}
              <div className="p-4 flex items-start gap-3" data-id="element-6026">
                <div className="flex items-center h-5 mt-0.5" data-id="element-6027">
                  <input id="notifyAdminClaimed" type="checkbox" checked={notifyAdminClaimed} onChange={e => setNotifyAdminClaimed(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-6028" />
                </div>
                <div className="flex-1" data-id="element-6029">
                  <label htmlFor="notifyAdminClaimed" className="text-sm font-medium text-[#0F172A] block" data-id="element-6030">
                    Notify admin when fully claimed
                  </label>
                  <p className="text-xs text-[#64748B] mt-0.5" data-id="element-6031">
                    You'll receive a notification when all stock is allocated
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Template Section */}
          <div className="pt-6 border-t border-[#E2E8F0]" data-id="element-6032">
            <div className="flex items-start gap-3" data-id="element-6033">
              <div className="flex items-center h-5 mt-0.5" data-id="element-6034">
                <input id="saveAsTemplate" type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-6035" />
              </div>
              <div className="flex-1 space-y-3" data-id="element-6036">
                <label htmlFor="saveAsTemplate" className="text-sm font-medium text-[#0F172A] block" data-id="element-6037">
                  Save as template for future use
                </label>
                {saveAsTemplate && <div className="animate-in fade-in slide-in-from-top-1 duration-200" data-id="element-6038">
                    <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g. Reserve Release Template" className="max-w-md" data-id="element-6039" />
                    <p className="text-xs text-[#64748B] mt-1.5" data-id="element-6040">
                      Saves product rules, eligibility, and notification
                      settings for reuse
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
  return <div className="h-screen flex flex-col bg-[#F1F5F9]" data-id="element-6041">
      {/* Toolbar */}
      <div className="h-16 bg-white border-b border-[#E2E8F0] px-6 flex items-center justify-between flex-shrink-0 z-10" data-id="element-6042">
        <div className="flex items-center gap-4" data-id="element-6043">
          <Button variant="ghost" onClick={() => isEditMode ? navigate(`/allocations/${editId}`) : navigate('/allocations')} className="text-[#64748B]" data-id="element-6044">
            Cancel
          </Button>
          <div className="h-6 w-px bg-[#E2E8F0]" data-id="element-6045" />
          <h1 className="text-lg font-semibold text-[#0F172A]" data-id="element-6046">
            {isEditMode ? 'Edit Allocation' : 'Create Allocation'}
          </h1>
        </div>

        <div className="flex items-center gap-3" data-id="element-6047">
          <Button variant="outline" onClick={handleSaveDraft} data-id="element-6048">
            Save Draft
          </Button>
          {activeStep === 4 ? <Button variant="primary" onClick={handlePublish} iconLeft={<Save className="w-4 h-4" data-id="element-6050" />} data-id="element-6049">
              {isEditMode ? 'Save Changes' : 'Publish Allocation'}
            </Button> : <Button variant="primary" onClick={handleNext} iconRight={<ArrowRight className="w-4 h-4" data-id="element-6052" />} data-id="element-6051">
              Next Step
            </Button>}
        </div>
      </div>

      {/* Step Progress Bar */}
      <div className="bg-white border-b border-[#E2E8F0]" data-id="element-6053">
        <div className="flex items-center justify-center max-w-4xl mx-auto py-6 px-8" data-id="element-6054">
          {steps.map((step, index) => {
          const status = activeStep > step.number ? 'completed' : activeStep === step.number ? 'active' : 'upcoming';
          const isLast = index === steps.length - 1;
          return <Fragment key={step.id} data-id="element-6055">
                <button onClick={() => handleStepClick(step.number)} className="flex items-center gap-3 transition-all duration-200 group" disabled={status === 'upcoming'} data-id="element-6056">
                  {/* Step Circle */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold transition-all duration-200 ${status === 'completed' ? 'bg-[#10B981] text-white' : status === 'active' ? 'bg-[#0EA5E9] text-white' : 'bg-[#E2E8F0] text-[#94A3B8]'}`} data-id="element-6057">
                    {status === 'completed' ? <Check className="w-5 h-5" data-id="element-6058" /> : <step.icon className="w-5 h-5" data-id="element-6059" />}
                  </div>

                  {/* Step Label */}
                  <span className={`text-base font-medium transition-colors duration-200 ${status === 'active' ? 'text-[#0F172A] font-semibold' : status === 'completed' ? 'text-[#475569]' : 'text-[#94A3B8]'}`} data-id="element-6060">
                    {step.label}
                  </span>
                </button>

                {/* Connector Line */}
                {!isLast && <div className={`flex-1 h-0.5 mx-4 max-w-[120px] transition-colors duration-200 ${status === 'completed' ? 'bg-[#10B981]' : 'bg-[#E2E8F0]'}`} data-id="element-6061" />}
              </Fragment>;
        })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-[#F1F5F9] p-8" data-id="element-6062">
        {activeStep === 1 && renderDetailsStep()}
        {activeStep === 2 && renderProductsStep()}
        {activeStep === 3 && renderEligibilityStep()}
        {activeStep === 4 && renderReviewStep()}
      </div>

      {/* Publish Confirmation Modal */}
      {showPublishModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-6063">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-6064">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-6065">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-6066">
                Publish Allocation
              </h3>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6" data-id="element-6067">
              <div data-id="element-6068">
                <p className="text-base font-bold text-[#0F172A] mb-4" data-id="element-6069">
                  {formData.name || 'Untitled Allocation'}
                </p>

                <div className="space-y-3" data-id="element-6070">
                  <div className="flex items-start gap-3" data-id="element-6071">
                    <Calendar className="w-5 h-5 text-[#64748B] flex-shrink-0" data-id="element-6072" />
                    <span className="text-sm text-[#475569]" data-id="element-6073">
                      Active from {formData.startDate || 'TBD'} —{' '}
                      {formData.endDate || 'TBD'}
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-6074">
                    <Wine className="w-5 h-5 text-[#64748B] flex-shrink-0" data-id="element-6075" />
                    <span className="text-sm text-[#475569]" data-id="element-6076">
                      {selectedProducts.reduce((sum, p) => sum + (Number(p.allocationQty) || 0), 0)}{' '}
                      units across {selectedProducts.length} products
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-6077">
                    <Users className="w-5 h-5 text-[#64748B] flex-shrink-0" data-id="element-6078" />
                    <span className="text-sm text-[#475569]" data-id="element-6079">
                      485 eligible members
                    </span>
                  </div>
                </div>
              </div>

              <div className="h-px bg-[#E2E8F0]" data-id="element-6080" />

              <div className="space-y-3" data-id="element-6081">
                <div className="flex items-start gap-3" data-id="element-6082">
                  <Mail className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" data-id="element-6083" />
                  <span className="text-sm text-[#0F172A]" data-id="element-6084">
                    Invitation emails will be sent to 485 members via Klaviyo
                  </span>
                </div>

                {earlyAccessEnabled && <div className="flex items-start gap-3 bg-[#E0F2FE] p-3 rounded-md border border-[#BAE6FD]" data-id="element-6085">
                    <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0" data-id="element-6086" />
                    <span className="text-sm text-[#0369A1]" data-id="element-6087">
                      {tiers.find(t => t.id === earlyAccessTier)?.name} tier
                      members will receive early access on{' '}
                      {formData.startDate ? new Date(new Date(formData.startDate).getTime() - 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short'
                }) : '2 days prior'}
                    </span>
                  </div>}
              </div>

              <div className="flex items-start gap-3 text-[#64748B]" data-id="element-6088">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" data-id="element-6089" />
                <p className="text-xs" data-id="element-6090">
                  Once published, the allocation window and eligibility rules
                  cannot be changed. Product quantities can still be adjusted.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex justify-end gap-3" data-id="element-6091">
              <Button variant="secondary" onClick={() => setShowPublishModal(false)} data-id="element-6092">
                Cancel
              </Button>
              {formData.startDate && new Date(formData.startDate) > new Date() && <Button variant="secondary" onClick={confirmPublish} data-id="element-6093">
                    Schedule for Start Date
                  </Button>}
              <Button variant="primary" onClick={confirmPublish} data-id="element-6094">
                Publish Now
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}